import { useRef, useState } from "react";
import { Upload } from "lucide-react";
import { api, ApiError } from "../../../lib/api";
import { toast } from "../../../stores/toast";
import { KycUploadedPreview } from "./KycUploadedPreview";
import { FlexxiMobileCaptureQrPanel, type FlexxiCaptureField } from "./FlexxiMobileCaptureQrPanel";

interface PrivateKycUploadFieldProps {
  label: string;
  hint?: string;
  value: string;
  onChange: (storageKey: string) => void;
  capture?: "user" | "environment";
  mobileCaptureField?: FlexxiCaptureField;
}

export function PrivateKycUploadField({
  label,
  hint,
  value,
  onChange,
  capture,
  mobileCaptureField,
}: PrivateKycUploadFieldProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const usePhoneQr =
    mobileCaptureField &&
    typeof window !== "undefined" &&
    !/Android|iPhone|iPad|Mobile/i.test(navigator.userAgent);

  async function uploadFile(file: File) {
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("files", file);
      const res = await api<{ keys: string[] }>("/uploads/private", { formData });
      const key = res.keys[0];
      if (!key) throw new Error("No file key returned");
      onChange(key);
      toast("success", "Document uploaded securely");
    } catch (err) {
      toast("error", err instanceof ApiError ? err.message : "Upload failed");
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  }

  const pickLabel = uploading ? "Uploading…" : value ? "Replace photo" : "Take photo or upload";

  return (
    <div className="space-y-2">
      <p className="text-xs font-medium text-white/80">{label}</p>
      {hint ? <p className="text-[11px] text-white/45">{hint}</p> : null}

      {usePhoneQr && mobileCaptureField ? (
        value ? (
          <div className="space-y-2">
            <KycUploadedPreview fileKey={value} />
            <button
              type="button"
              className="text-[11px] font-semibold text-brand-pink hover:underline"
              onClick={() => onChange("")}
            >
              Replace using phone
            </button>
          </div>
        ) : (
          <FlexxiMobileCaptureQrPanel field={mobileCaptureField} onFileKey={onChange} />
        )
      ) : (
        <>
          {value ? <KycUploadedPreview fileKey={value} /> : null}
          <button
            type="button"
            disabled={uploading}
            onClick={() => inputRef.current?.click()}
            className="flex w-full items-center justify-center gap-2 rounded-xl border border-dashed border-white/15 px-4 py-4 text-xs text-white/45 hover:border-brand-pink/40 disabled:opacity-50"
          >
            <Upload size={16} />
            {pickLabel}
          </button>
          <input
            ref={inputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            capture={capture}
            className="hidden"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) void uploadFile(file);
            }}
          />
        </>
      )}
    </div>
  );
}
